// src/components/DetailSidebarIntel.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ShieldAlert,
  X,
  Copy,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";
import { redirectToLogin } from "../utils/auth";

/**
 * IntelDetailSidebar
 *
 * Props:
 * - open, onClose, postId, jwtToken, apiUrl (detail endpoint)
 * - licenseUrl (subscription/entitlements endpoint)
 * - size: "wide" | "normal" (default "normal")
 * - backdropZ: number (default 400)
 * - panelZ: number (default 500)
 * - onUpgradeClick: () => void  (default navigates to "/upgrades")
 *
 * Notes:
 * - If subscription check says Threat Feed is not enabled, we DO NOT fetch the detail.
 *   We render blurred mock content + a warning to upgrade.
 */
export default function IntelDetailSidebar({
  open,
  onClose,
  postId,
  jwtToken,
  apiUrl = "https://whiteintel.io/api/regular/app/v2/threatfeed/get_feed.php",
  licenseUrl = "https://whiteintel.io/api/regular/app/v2/account/subscription.php",
  size = "normal",
  backdropZ = 400,
  panelZ = 500,
  onUpgradeClick,
  source
}) {
  // subscription / license
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState("");
  const [hasThreatFeedAccess, setHasThreatFeedAccess] = useState(null); // null=unknown,true/false

  // detail
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [post, setPost] = useState(null);
  const abortRef = useRef(null);

  const canOpen = open && Boolean(postId) && Boolean(jwtToken);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // --- Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  // --- Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // --- Reset on close / id change
  useEffect(() => {
    if (!open) {
      setPost(null);
      setErr("");
      setSubError("");
      setHasThreatFeedAccess(null);
      setLightboxOpen(false);
      setLightboxIndex(0);
    }
  }, [open, postId]);

  // --- 1) Fetch subscription first
  useEffect(() => {
    if (!canOpen) return;

    setSubLoading(true);
    setSubError("");
    (async () => {
      try {
        const res = await fetch(licenseUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          // Body can be empty or `{}` — keep flexible for your backend
          body: JSON.stringify({}),
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const allowed = extractThreatFeedAccess(json);
        setHasThreatFeedAccess(Boolean(allowed));
      } catch (e) {
        // If the subscription call fails, default to "no access" but show the error
        setHasThreatFeedAccess(false);
        setSubError(e.message || "Failed to verify subscription");
      } finally {
        setSubLoading(false);
      }
    })();
  }, [canOpen, jwtToken, licenseUrl]);

  // --- 2) Fetch intel detail ONLY if access is granted
  useEffect(() => {
    if (!canOpen) return;
    if (hasThreatFeedAccess !== true) return; // wait; or skip when false

    setLoading(true);
    setErr("");
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const res = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({ metric: "detail", id: postId, source: source }),
          signal: controller.signal,
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        setPost(raw?.data ?? raw ?? null);
      } catch (e) {
        if (e.name !== "AbortError") setErr(e.message || "Failed to load post");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [canOpen, apiUrl, jwtToken, postId, hasThreatFeedAccess]);

  // mock content when locked
  const locked = hasThreatFeedAccess === false;
  const effectivePost = locked ? mockPost(postId) : post;

  // Derived meta
  const meta = useMemo(() => {
    const v = (x) => (x === null || x === undefined || x === "" ? "—" : x);
    const p = effectivePost || {};
    return {
      id: v(p.id ?? postId),
      title: p.title || `Intel Post #${postId}`,
      published_at: v(fmtISO(p.published_at)),
      network: v(p.network),
      category: v(p.category),
      org: v(p.victim_organization),
      site: v(p.victim_site),
      domain: v(p.victim_domain),
      industry: v(p.victim_industry),
      country: v(p.victim_country),
      url: p.published_url || "",
      tags: Array.isArray(p.tags) ? p.tags : [],
      actors: Array.isArray(p.threat_actors) ? p.threat_actors : [],
      screenshots: Array.isArray(p.screenshots) ? p.screenshots : [], // <-- added
      content: p.content || "",
    };
  }, [effectivePost, postId]);

  /* ----------------- RENDER (PORTALS) ----------------- */
  return (
    <>
      {/* Backdrop */}
      {open &&
        createPortal(
          <div
            role="presentation"
            onClick={onClose}
            aria-hidden="true"
            style={{ zIndex: backdropZ }}
            className="fixed inset-0 bg-black/60 transition-opacity opacity-100"
          />,
          document.body
        )}

      {/* Panel */}
      {open &&
        createPortal(
          <aside
            role="dialog"
            aria-modal="true"
            style={{ zIndex: panelZ }}
            className={`fixed right-0 top-0 h-full w-full
                        ${size === "wide" ? "max-w-4xl" : "max-w-2xl"}
                        border-l border-neutral-800 
                        bg-[#000414] 
                        shadow-[0_0_15px_rgba(56,189,248,0.05)]
                        transform transition-transform duration-300 translate-x-0`}
          >
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-neutral-800">
              <div>
                <div className="flex items-center gap-2 text-sm text-neutral-400">
                  <ShieldAlert className="h-4 w-4" />
                  <span>Threat Feed — {meta.id}</span>
                </div>
                <h2 className="mt-1 text-xl font-semibold">{meta.title}</h2>
              </div>

              <div className="flex items-center gap-3">
                <IconBtn title="Close" onClick={onClose}>
                  <X className="h-4 w-4" />
                </IconBtn>
              </div>
            </div>

            {/* Top callouts */}
            <div className="px-5 pt-4">
              {subLoading && (
                <div className="mb-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-neutral-300">
                  Checking your subscription…
                </div>
              )}
              {subError && (
                <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  Subscription check failed: {subError}
                </div>
              )}
              {locked && <UpgradeCallout onClick={onUpgradeClick} />}
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-5 pt-3 pb-24 h-[calc(100%-112px)] relative">
              {/* Loading / Error / Body */}
              {loading && !locked ? (
                <Loader />
              ) : err && !locked ? (
                <div className="p-5 text-red-400 text-sm">Error: {err}</div>
              ) : (
                <div
                  className={`space-y-8 ${
                    locked ? "relative pointer-events-none select-none" : ""
                  }`}
                >
                  <div className={locked ? "blur-sm" : ""}>
                    <Section title="Published">
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <dt className="text-[11px] uppercase tracking-wide text-neutral-400">
                            Date
                          </dt>
                          <dd className="mt-1 text-sm text-neutral-100">
                            {meta.published_at}
                          </dd>
                        </div>

                        <div className="sm:col-span-1">
                          <dt className="text-[11px] uppercase tracking-wide text-neutral-400">
                            Source URL
                          </dt>
                          <dd className="mt-1 text-sm text-neutral-100 break-all">
                            {meta.url ? (
                              <div className="flex items-center gap-2">
                                <a
                                  href={meta.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sky-300 hover:text-sky-200 underline"
                                >
                                  {meta.url}
                                </a>
                                <CopyBtn text={meta.url} />
                              </div>
                            ) : (
                              "—"
                            )}
                          </dd>
                        </div>
                      </dl>
                    </Section>

                    <Section title="Meta Data">
                      <MetaGrid
                        items={[
                          { label: "Network", value: meta.network },
                          { label: "Category", value: meta.category },
                          { label: "Victim Org", value: meta.org },
                          { label: "Victim Site", value: meta.site },
                          { label: "Victim Domain", value: meta.domain },
                          { label: "Industry", value: meta.industry },
                          { label: "Country", value: meta.country },
                          {
                            label: "Threat Actor",
                            value: Array.isArray(meta.actors)
                              ? meta.actors.join(", ")
                              : meta.actors,
                          },
                        ]}
                      />
                    </Section>

                    {meta.content && (
                      <Section title="Content">
                        <div className="text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed">
                          {meta.content}
                        </div>
                      </Section>
                    )}

                    {meta.screenshots.length > 0 && (
                      <Section title="Screenshots">
                        <ScreenshotGrid
                          images={meta.screenshots}
                          locked={locked}
                          onOpen={(idx) => {
                            setLightboxIndex(idx);
                            setLightboxOpen(true);
                          }}
                        />
                      </Section>
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>,
          document.body
        )}

      {/* Lightbox (outside the panel so it can go full-screen) */}
      {open &&
        lightboxOpen &&
        createPortal(
          <LightboxModal
            images={meta.screenshots}
            index={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
            onPrev={() =>
              setLightboxIndex(
                (i) => (i - 1 + meta.screenshots.length) % meta.screenshots.length
              )
            }
            onNext={() =>
              setLightboxIndex((i) => (i + 1) % meta.screenshots.length)
            }
          />,
          document.body
        )}
    </>
  );
}

/* ---------------- Helpers ---------------- */

function UpgradeCallout({ onClick }) {
  const go = () => {
    if (typeof onClick === "function") return onClick();
    try {
      window.location.href = "/upgrades";
    } catch {}
  };
  return (
    <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-100">
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 mt-0.5" />
        <div>
          <div className="font-semibold">Threat Feed is locked</div>
          <div className="text-sm opacity-90">
            Your current subscription doesn’t include Threat Feed. Upgrade to
            unlock full intel details, sources and historical context.
          </div>
          <div className="mt-2">
            <button
              onClick={go}
              className="rounded-md border border-amber-500/40 bg-transparent px-3 py-1.5 text-sm hover:bg-amber-500/10"
            >
              Upgrade plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Try hard to read “threat feed allowed” from various API shapes.
function extractThreatFeedAccess(json) {
  try {
    if (!json || typeof json !== "object") return false;
    // 1) flat: { threat_feed: 1 }
    if (Object.prototype.hasOwnProperty.call(json, "threat_feed")) {
      const v = json.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
    // 2) nested: { data: { threat_feed: 1 } }
    if (json.data && typeof json.data === "object" && "threat_feed" in json.data) {
      const v = json.data.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
    // 3) other: { subscription: { threat_feed: 1 } }
    if (
      json.subscription &&
      typeof json.subscription === "object" &&
      "threat_feed" in json.subscription
    ) {
      const v = json.subscription.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
  } catch (_) {}
  return false;
}

function mockPost(postId) {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: postId,
    title: "Threat Feed Preview (locked)",
    published_at: today,
    network: "telegram",
    category: "intel (preview)",
    victim_organization: "Acme Corp",
    victim_site: "acme.example",
    victim_domain: "acme.example",
    victim_industry: "Technology",
    victim_country: "US",
    published_url: "https://whiteintel.io/",
    tags: ["ransomware", "ddos", "dataleak"],
    threat_actors: ["killnet", "alphv"],
    screenshots: [
      "https://via.placeholder.com/800x450.png?text=Preview+1",
      "https://via.placeholder.com/800x450.png?text=Preview+2",
    ],
    content:
      "This is a preview of Threat Feed content. Upgrade your plan to unlock the full post body, artifacts, indicators and original source links.",
  };
}

/* ---------------- UI bits ---------------- */

function IconBtn({ children, title, onClick, disabled }) {
  return (
    <button
      className={`p-2 rounded ${
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5"
      }`}
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-4 first:mt-0">
      <h3 className="mb-4 text-sm font-semibold tracking-wide text-neutral-400 uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function MetaGrid({ items = [] }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map(({ label, value }, idx) => (
        <div key={`${label}-${idx}`} className="p-3">
          <dt className="text-[11px] uppercase tracking-wide text-neutral-400">
            {label}
          </dt>
          <dd className="mt-1 text-sm text-neutral-100">
            {value === null || value === undefined || value === ""
              ? "—"
              : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ChipRow({ items = [], color = "tag", ariaLabel }) {
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label={ariaLabel}>
      {items.map((text, i) => (
        <span
          key={`${text}-${i}`}
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${
            color === "actor"
              ? "border-purple-500/30 text-purple-200 bg-purple-500/10"
              : tagClass(String(text).toLowerCase())
          }`}
        >
          {text}
        </span>
      ))}
    </div>
  );
}

function Loader() {
  return (
    <div className="p-5 space-y-5">
      <div className="h-5 w-36 bg-neutral-800/60 animate-pulse rounded" />
      <div className="space-y-3">
        <div className="h-4 w-64 bg-neutral-800/60 animate-pulse rounded" />
        <div className="h-4 w-48 bg-neutral-800/60 animate-pulse rounded" />
      </div>
      <div className="space-y-2">
        <div className="h-4 w-full bg-neutral-800/60 animate-pulse rounded" />
        <div className="h-4 w-5/6 bg-neutral-800/60 animate-pulse rounded" />
        <div className="h-4 w-4/6 bg-neutral-800/60 animate-pulse rounded" />
      </div>
    </div>
  );
}

function CopyBtn({ text }) {
  return (
    <button
      onClick={() => navigator.clipboard.writeText(String(text))}
      className="p-1 rounded hover:bg-white/5"
      title="Copy to clipboard"
    >
      <Copy className="h-4 w-4 text-neutral-400 hover:text-white" />
    </button>
  );
}

/** Grid of clickable thumbnails */
function ScreenshotGrid({ images = [], locked = false, onOpen }) {
  return (
    <div
      className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${
        locked ? "pointer-events-none select-none blur-sm" : ""
      }`}
    >
      {images.map((src, i) => (
        <button
          key={src + i}
          type="button"
          className="group relative aspect-video overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900"
          onClick={() => onOpen?.(i)}
          aria-label={`Open screenshot ${i + 1}`}
        >
          <img
            src={src}
            alt={`Screenshot ${i + 1}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
          <div className="pointer-events-none absolute inset-0 ring-0 ring-inset ring-transparent group-hover:ring-white/10" />
        </button>
      ))}
    </div>
  );
}

/** Full-screen lightbox with keyboard and arrows */
function LightboxModal({ images = [], index = 0, onClose, onPrev, onNext }) {
  // Prevent background scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC / ← / →
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") onPrev?.();
      else if (e.key === "ArrowRight") onNext?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const src = images[index] || "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      {/* Stop click-through on panel */}
      <div
        className="relative max-w-[95vw] max-h-[90vh] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 rounded-full bg-white/10 p-2 hover:bg-white/20"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-white" />
        </button>

        {/* Prev / Next */}
        {images.length > 1 && (
          <>
            <button
              onClick={onPrev}
              className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 hover:bg-white/20"
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
            <button
              onClick={onNext}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 hover:bg-white/20"
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>
          </>
        )}

        {/* Image */}
        <div className="flex items-center justify-center">
          <img
            src={src}
            alt={`Screenshot ${index + 1}`}
            className="max-h-[85vh] max-w-[90vw] rounded-lg border border-white/10 object-contain shadow-2xl"
          />
        </div>

        {/* Actions (download, counter) */}
        <div className="mt-3 flex items-center justify-between text-neutral-200">
          <div className="text-xs opacity-80">
            {index + 1} / {images.length}
          </div>
          {src && (
            <a
              href={src}
              download
              className="inline-flex items-center gap-1 rounded-md border border-white/20 px-2 py-1 text-sm hover:bg-white/10"
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- utils ---------------- */

function fmtISO(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toISOString().slice(0, 10);
}

function tagClass(t) {
  switch (t) {
    case "telegram":
      return "border-sky-500/30 text-sky-200 bg-sky-500/10";
    case "ransomware":
      return "border-red-500/30 text-red-200 bg-red-500/10";
    case "ddos":
    case "ddos attack":
      return "border-amber-500/30 text-amber-200 bg-amber-500/10";
    case "dataleak":
    case "data breach":
    case "data leak":
      return "border-emerald-500/30 text-emerald-200 bg-emerald-500/10";
    default:
      return "border-neutral-700 text-neutral-200 bg-neutral-900/40";
  }
}
